import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getUserRoles } from '../../hooks/useAuth';
import LoadingSpinner from './UI/LoadingSpinner';

// Returns true if the user holds ANY of the required roles.
// required can be a single string or an array of strings.
function hasRequiredRole(userProfile, required) {
  if (!required) return true;
  const requiredArray = Array.isArray(required) ? required : [required];
  const userRoles = getUserRoles(userProfile);
  return requiredArray.some(role => userRoles.includes(role));
}

function AccessDenied() {
  const navigate = useNavigate();
  return (
    <div className="access-denied">
      <div className="access-denied-box">
        <h1>Access Denied</h1>
        <p>
          You do not have the required access to view this page.
          Contact your system administrator if you believe this is an error.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </button>
          <button
            className="btn btn-secondary"
            onClick={async () => {
              const { signOutUser } = await import('../../services/authService');
              await signOutUser();
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProtectedRoute({ children, requireAdmin = false, requiredRole }) {
  const { user, userProfile, loading, authReady } = useAuth();
  const location = useLocation();

  // Initial load — no user in state yet, show full-page spinner
  if (loading && !user) {
    return <LoadingSpinner fullPage />;
  }

  if (!user) {
    const isCompanyRoute = location.pathname.startsWith('/company');
    return <Navigate to={isCompanyRoute ? '/company/login' : '/login'} replace />;
  }

  // Authenticated, but the profile/claims are still resolving. An unresolved
  // profile is NOT an unauthorised one. Deciding here rendered AccessDenied
  // mid-load (whose "Go to Dashboard" button was an escape hatch straight into
  // the internal shell), and on a route with no role check it rendered the
  // shell itself before the roles were known. Wait instead — authReady is the
  // signal useAuth already publishes for exactly this. The !user check above
  // runs first because authReady is also false when signed out.
  if (!authReady) {
    return <LoadingSpinner fullPage />;
  }

  // Company users must stay in /company/* routes
  const userRoles = getUserRoles(userProfile);
  if (userRoles.includes('company') && !location.pathname.startsWith('/company')) {
    return <Navigate to="/company/dsq" replace />;
  }

  // requireAdmin is a convenience alias for requiredRole="admin"
  const roleToCheck = requiredRole || (requireAdmin ? 'admin' : null);

  if (roleToCheck && !hasRequiredRole(userProfile, roleToCheck)) {
    return <AccessDenied />;
  }

  return children;
}
